import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ChevronLeft, ChevronRight, Share2, Download, Play, Pause, Maximize2 } from 'lucide-react'

const Lightbox = ({ isOpen, onClose, media = [], startIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(startIndex)
  const [isPlaying, setIsPlaying] = useState(false)

  const currentMedia = media[currentIndex]

  useEffect(() => {
    setCurrentIndex(startIndex)
  }, [startIndex, isOpen])

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          onClose()
          break
        case 'ArrowLeft':
          goToPrevious()
          break
        case 'ArrowRight':
          goToNext()
          break
        default:
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, currentIndex])

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : media.length - 1))
  }, [media.length])

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < media.length - 1 ? prev + 1 : 0))
  }, [media.length])

  const handleShare = async () => {
    if (navigator.share && currentMedia) {
      try {
        await navigator.share({
          title: currentMedia.caption || 'Church Gallery',
          url: currentMedia.url,
        })
      } catch (err) {
        console.log('Share cancelled')
      }
    }
  }

  const getYouTubeId = (url) => {
    const match = url?.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)
    return match ? match[1] : null
  }

  const getVimeoId = (url) => {
    const match = url?.match(/vimeo\.com\/(\d+)/)
    return match ? match[1] : null
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
        onClick={onClose}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 text-white">
          <div className="text-sm">
            {currentIndex + 1} / {media.length}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleShare()
              }}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <Share2 className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                onClose()
              }}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center relative px-4">
          {/* Previous Button */}
          {media.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                goToPrevious()
              }}
              className="absolute left-4 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <ChevronLeft className="w-8 h-8 text-white" />
            </button>
          )}

          {/* Media Display */}
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="max-w-5xl max-h-[80vh] w-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {currentMedia?.type === 'video' ? (
              <div className="w-full aspect-video bg-black rounded-lg overflow-hidden">
                {getYouTubeId(currentMedia.url) ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${getYouTubeId(currentMedia.url)}?autoplay=1`}
                    className="w-full h-full"
                    allowFullScreen
                    allow="autoplay; encrypted-media"
                  />
                ) : getVimeoId(currentMedia.url) ? (
                  <iframe
                    src={`https://player.vimeo.com/video/${getVimeoId(currentMedia.url)}?autoplay=1`}
                    className="w-full h-full"
                    allowFullScreen
                    allow="autoplay"
                  />
                ) : (
                  <video
                    src={currentMedia.url}
                    controls
                    autoPlay
                    className="w-full h-full object-contain"
                  />
                )}
              </div>
            ) : (
              <img
                src={currentMedia?.url}
                alt={currentMedia?.caption || ''}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
            )}
          </motion.div>

          {/* Next Button */}
          {media.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                goToNext()
              }}
              className="absolute right-4 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <ChevronRight className="w-8 h-8 text-white" />
            </button>
          )}
        </div>

        {/* Caption & Thumbnails */}
        <div className="p-4 text-center">
          {currentMedia?.caption && (
            <p className="text-white/80 mb-4">{currentMedia.caption}</p>
          )}

          {/* Thumbnail Strip */}
          {media.length > 1 && (
            <div className="flex justify-center gap-2 overflow-x-auto pb-2">
              {media.slice(0, 10).map((item, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation()
                    setCurrentIndex(idx)
                  }}
                  className={`relative w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 transition-all ${
                    idx === currentIndex
                      ? 'ring-2 ring-white scale-110'
                      : 'opacity-50 hover:opacity-100'
                  }`}
                >
                  {item.type === 'video' ? (
                    <div className="w-full h-full bg-dark-200 flex items-center justify-center">
                      <Play className="w-6 h-6 text-white" />
                    </div>
                  ) : (
                    <img
                      src={item.thumbnail || item.url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  )}
                </button>
              ))}
              {media.length > 10 && (
                <div className="w-16 h-16 rounded-lg bg-white/10 flex items-center justify-center text-white text-sm">
                  +{media.length - 10}
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

export default Lightbox
